import React from 'react';
import {Redirect, Route, Switch, useLocation} from 'react-router-dom';
import classnames from 'classnames';
import Survey from 'src/components/Survey';
import {withFeatureFlags, WithFeatureFlagsProps} from 'src/hooks/useFeatureFlags';
import CollegeMajors from 'src/views/admin/CollegeMajors';
import CommonTests from 'src/views/admin/CommonTests';
import CourseListings from 'src/views/admin/CourseListings';
import DistrictAdmin from 'src/views/admin/DistrictAdmin';
import EditResourceSection from 'src/views/admin/EditResourceSection';
import EducatorVerification from 'src/views/admin/EducatorVerification';
import EducatorInspection from 'src/views/admin/EducatorVerification/EducatorInspection';
import FileUploader from 'src/views/admin/FileUploader';
import HallwayMonitor from 'src/views/admin/HallwayMonitor';
import Home from 'src/views/admin/home';
import HonorAwards from 'src/views/admin/HonorAwards';
import InternalUsers from 'src/views/admin/InternalUsers';
import ResourceSections from 'src/views/admin/ResourceSections';
import ScheduledJobs from 'src/views/admin/ScheduledJobs';
import TimeSensitiveItemTypes from 'src/views/admin/TimeSensitiveItemTypes';
import EditForm from 'src/views/admin/TimeSensitiveItemTypes/EditForm';
import Form from 'src/views/admin/TimeSensitiveItemTypes/Form';
import UGSSTools from 'src/views/admin/UGSSTools';
import UniversityCollegePortalUsers from 'src/views/admin/UniversityCollegePortalUsers';
import CommunityCollegeSearch from '../CollegeListingCampuses';
import EditCollegeProfile from '../EditCollegeProfile';
import ToolbarNotificationsAdmin from '../ToolbarNotificationsAdmin';
import CreateOrEditToolbarNotificationForm from '../ToolbarNotificationsAdmin/CreateOrEditToolbarNotificationForm';
import {FEATURE_FLAGS, MANAGE_INTERNAL_USERS, MANAGE_SFTP_USERS} from '../constants';
import CollegeRanks from '../college-ranks';
import AdminScheduledReports from 'src/views/admin/AdminScheduledReports';
import AdminUniversity from 'src/apps/AdminUniversity';
import AdminCollegeListings from 'src/views/admin/CollegeListings';
import SftpUsers from 'src/views/admin/sftp-users';
import ExperienceEverywhereTenants from 'src/views/admin/experience-everywhere-tenants';
import PartnerProvisioning from 'src/views/admin/partner-provisioning';
import NewCollegeListing from 'src/views/admin/new-college-listing';
import AccessOrganizations from 'src/views/admin/AccessOrganizations';
import EditAccessOrganization from 'src/views/admin/AccessOrganizations/edit-access-organization';
import FeatureFlags from '../feature-flags';
import FeatureFlagModal from '../feature-flags/feature-flag-modal';

const FULL_WIDTH_PATHS = [
  '/admin/tools/feature-flags',
  '/admin/tools/experience-everywhere-tenants',
];

function useHasContainer(): boolean {
  const {pathname} = useLocation();
  return !FULL_WIDTH_PATHS.some((path) => pathname.startsWith(path));
}

const Routes = ({featureFlags}: WithFeatureFlagsProps) => {
  const hasContainer = useHasContainer();

  return (
    <div className={classnames({container: hasContainer})}>
      <div className="row">
        <Switch>
          <Route path="/admin/home" component={Home} />
          <Route path="/admin/tools/access-organizations" exact component={AccessOrganizations} />
          <Route
            path="/admin/tools/access-organizations/:id/edit"
            component={EditAccessOrganization}
          />
          <Route
            path="/admin/tools/time-sensitive-item-types"
            exact
            component={TimeSensitiveItemTypes}
          />
          <Route path="/admin/tools/time-sensitive-item-types/new" exact component={Form} />
          <Route path="/admin/tools/time-sensitive-item-types/:id" exact component={EditForm} />
          <Route path="/admin/tools/ugss" component={UGSSTools} />
          <Route path="/admin/tools/scheduled-jobs" component={ScheduledJobs} />
          <Route path="/admin/tools/community-college-search" component={CommunityCollegeSearch} />
          <Route path="/admin/tools/upload-file" component={FileUploader} />
          <Route path="/admin/tools/resource-sections/:id" component={EditResourceSection} />
          <Route path="/admin/tools/resource-sections" component={ResourceSections} />
          <Route path="/admin/tools/district-admins" component={DistrictAdmin} />
          {featureFlags[MANAGE_INTERNAL_USERS] && (
            <Route path="/admin/tools/internal-users" component={InternalUsers} />
          )}
          <Route path="/admin/tools/hallway-monitor" component={HallwayMonitor} />
          <Route path="/admin/tools/common-tests" component={CommonTests} />
          <Route path="/admin/tools/course-listings" component={CourseListings} />
          <Route path="/admin/tools/college-majors" component={CollegeMajors} />
          <Route path="/admin/tools/college-ranks" component={CollegeRanks} />
          <Route path="/admin/tools/honors-and-awards" component={HonorAwards} />
          <Route
            path="/admin/tools/college-portal-users/:slug?"
            component={UniversityCollegePortalUsers}
          />
          <Route path="/admin/tools/colleges/new" component={NewCollegeListing} />
          <Route path="/admin/tools/colleges/:id" component={EditCollegeProfile} />
          <Route
            path="/admin/tools/experience-everywhere-tenants"
            component={ExperienceEverywhereTenants}
          />
          <Route path="/admin/tools/partner-provisioning" component={PartnerProvisioning} />
          <Route path="/survey/:id/:page?" component={Survey} />
          {featureFlags.editingFeatureFlags && (
            <Route path="/admin/tools/feature-flags">
              <FeatureFlags />
              <Route path="/admin/tools/feature-flags/:id" component={FeatureFlagModal} />
            </Route>
          )}
          <Route
            path="/admin/tools/toolbar-notifications/:id"
            component={CreateOrEditToolbarNotificationForm}
          />
          <Route path="/admin/tools/toolbar-notifications" component={ToolbarNotificationsAdmin} />
          <Route path="/admin/tools/educator-verification" component={EducatorVerification} />
          <Route path="/admin/tools/educator-inspection" component={EducatorInspection} />
          {featureFlags.editingFeatureFlags && (
            <Route path="/admin/tools/scheduled-reports" component={AdminScheduledReports} />
          )}
          <Route path="/admin/university/edit" component={AdminUniversity} />
          <Route path="/admin/university/new" component={AdminUniversity} />
          <Route path="/admin/college-listings" component={AdminCollegeListings} />
          {featureFlags[MANAGE_SFTP_USERS] && (
            <Route path="/admin/tools/sftp-users" component={SftpUsers} />
          )}
          <Redirect path="*" to="/admin/home" />
        </Switch>
      </div>
    </div>
  );
};

export default withFeatureFlags(
  Routes,
  [...FEATURE_FLAGS, MANAGE_INTERNAL_USERS, 'editing_feature_flags'],
  false
);
